import type { History } from 'history';
import { compact, uniq } from 'lodash';
import isEqual from 'lodash/isEqual';
import queryString from 'query-string';
import { useEffect, useRef } from 'react';
import type { RecoilState } from 'recoil';
import { useRecoilValue } from 'recoil';
import { useScopedSetRecoilState } from 'venn-state';
import { getQueryParams, useHasFF } from 'venn-utils';

/** Keeps a library selection atom in sync with the `selectedIds` URL param.
 *  Shared by useLibraryStateURLSynchronizer.ts and usePrivateLibraryStateURLSynchronizer.ts.
 */
export const useLibrarySelectionUrlSync = (
  history: History<{ shouldListenerIgnore?: boolean }>,
  selectionAtom: RecoilState<string[]>,
) => {
  const selectedIds = useRecoilValue(selectionAtom);
  const setSelectedIds = useScopedSetRecoilState(selectionAtom);
  const persistSelection = useHasFF('data_library_persist_selection_ff');

  useEffect(() => {
    setSelectedIds(getSelectedIdsFromUrl(history.location.search));
    // this should happen on mount only, and the eslint rule is intentionally disabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Watches for updates to history.location.search and updates selectedIds accordingly
    return history.listen(({ state, search }) => {
      if (state?.shouldListenerIgnore) {
        return;
      }
      setSelectedIds(getSelectedIdsFromUrl(search));
    });
  }, [history, setSelectedIds]);

  const pendingSeededSelection = useRef<string[] | null>(getSelectedIdsFromUrl(history.location.search));

  useEffect(() => {
    // The mount-seed effect's setSelectedIds isn't visible until a later render, so hold off writing
    // until the atom reflects what the URL was seeded with - otherwise we'd strip the ids back out.
    // Resolved before the flag check so a flag arriving late (features hydrate after mount) doesn't
    // leave the latch stuck and suppress every later write.
    if (pendingSeededSelection.current !== null) {
      if (isEqual(pendingSeededSelection.current, selectedIds)) {
        pendingSeededSelection.current = null;
      }
      return;
    }
    if (!persistSelection) {
      return;
    }
    const path = history.location.pathname;
    if (!path || isEqual(getSelectedIdsFromUrl(history.location.search), selectedIds)) {
      return;
    }
    const queryParams = getQueryParams(history.location.search);
    history.replace(`${path}?${queryString.stringify({ ...queryParams, selectedIds })}`, {
      shouldListenerIgnore: true,
    });
  }, [selectedIds, history, persistSelection]);
};

export const getSelectedIdsFromUrl = (search: string): string[] => {
  const { selectedIds } = getQueryParams(search);
  return uniq(compact(typeof selectedIds === 'string' ? [selectedIds] : selectedIds));
};
